import { collection, doc, getDocs } from "firebase/firestore";
import moment from "moment-timezone";

// Format date to YYYY-MM-DD in GMT+7 timezone
export const formatDateGMT7 = (date) => {
  // Use moment-timezone to set the date in Asia/Ho_Chi_Minh (GMT+7) timezone
  const gmt7Date = moment.tz(date, "Asia/Ho_Chi_Minh");
  return gmt7Date.format("YYYY-MM-DD");
};

// Get today's date in GMT+7 timezone
export const getTodayGMT7 = () => {
  const today = new Date();
  today.setHours(today.getHours() + 7); // Adjust to GMT+7
  return new Date(today.setHours(0, 0, 0, 0)); // Set to midnight
};

// Generate all occurrences for a recurring event
export const generateOccurrences = (startDate, endDate, type, days = []) => {
  const MAX_OCCURRENCES = 1000; // Prevent infinite loops or excessive computation
  const occurrences = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Validate inputs
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
    return occurrences;
  }

  // For weekly recurrence, use the start date's day if no days are provided
  const recurrenceDays =
    type === "weekly" && days.length === 0
      ? [start.getDay()]
      : days.map(Number).sort((a, b) => a - b);

  // Helper function to add a date to occurrences
  const addOccurrence = (date) => {
    if (occurrences.length >= MAX_OCCURRENCES) return false;
    if (date <= end && date >= start) {
      occurrences.push(new Date(date));
      return true;
    }
    return true;
  };

  // Daily recurrence
  if (type === "daily") {
    let current = new Date(start);
    while (current <= end && occurrences.length < MAX_OCCURRENCES) {
      addOccurrence(current);
      current.setDate(current.getDate() + 1);
    }
  }

  // Weekly recurrence
  else if (type === "weekly") {
    let current = new Date(start);
    // Start from the first selected day on or after the start date
    while (current <= end && occurrences.length < MAX_OCCURRENCES) {
      for (const day of recurrenceDays) {
        const nextDate = new Date(current);
        // Calculate days until the next selected day
        const daysUntilDay = (day - current.getDay() + 7) % 7;
        nextDate.setDate(current.getDate() + daysUntilDay);

        if (nextDate <= end && nextDate >= start) {
          addOccurrence(nextDate);
        }
      }
      // Move to the start of the next week
      current.setDate(current.getDate() + 7);
    }
  }

  // Monthly recurrence
  else if (type === "monthly") {
    let current = new Date(start);
    while (current <= end && occurrences.length < MAX_OCCURRENCES) {
      addOccurrence(current);
      // Move to the same day in the next month
      const nextMonth = current.getMonth() + 1;
      current.setMonth(nextMonth);
      // Handle cases where the next month doesn't have this day (e.g., Jan 31 -> Feb 28)
      if (current.getMonth() !== nextMonth % 12) {
        current.setDate(0); // Move to the last day of the previous month
      }
    }
  }

  // Sort occurrences chronologically and remove duplicates
  return occurrences
    .sort((a, b) => a - b)
    .filter(
      (date, index, self) =>
        index === 0 || date.getTime() !== self[index - 1].getTime()
    );
};

// Weekday information for display purposes
export const weekDays = [
  { id: 0, name: "Sunday", shortName: "Sun" },
  { id: 1, name: "Monday", shortName: "Mon" },
  { id: 2, name: "Tuesday", shortName: "Tue" },
  { id: 3, name: "Wednesday", shortName: "Wed" },
  { id: 4, name: "Thursday", shortName: "Thu" },
  { id: 5, name: "Friday", shortName: "Fri" },
  { id: 6, name: "Saturday", shortName: "Sat" },
];

// Helper to format recurrence information for display
export const formatRecurrenceInfo = (booking) => {
  if (!booking.isRecurring) return "One-time";

  let info = `Recurring ${booking.recurrenceType}`;

  if (booking.recurrenceEndDate) {
    info += ` until ${new Date(booking.recurrenceEndDate).toLocaleDateString(
      "en-CA"
    )}`;
  }

  if (booking.recurrenceDays?.length > 0) {
    const dayNames = booking.recurrenceDays
      .map((d) => weekDays.find((wd) => wd.id === d)?.shortName)
      .filter(Boolean);
    info += ` on ${dayNames.join(", ")}`;
  }

  return info;
};

export const checkTimeSlotAvailability = async (
  booking,
  roomId,
  dbInstance,
  excludeBookingId = null
) => {
  const {
    date,
    startTime,
    endTime,
    isRecurring,
    recurrenceType,
    recurrenceEndDate,
    recurrenceDays,
  } = booking;

  if (!date || !startTime || !endTime || !roomId) {
    throw new Error("Missing required booking details");
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error("Invalid date format");
  }

  if (!/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime)) {
    throw new Error("Invalid time format");
  }

  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);
  const newStart = startHours * 60 + startMinutes;
  const newEnd = endHours * 60 + endMinutes;

  if (newStart >= newEnd) {
    throw new Error("End time must be after start time");
  }

  let occurrences;
  try {
    occurrences = isRecurring
      ? generateOccurrences(
          new Date(date),
          new Date(recurrenceEndDate),
          recurrenceType,
          recurrenceDays
        )
      : [new Date(date)];
  } catch {
    throw new Error("Invalid recurrence settings");
  }

  try {
    const roomRef = doc(dbInstance, "rooms", roomId);
    const bookingsRef = collection(roomRef, "bookings");
    const allBookingsSnapshot = await getDocs(bookingsRef);

    const allBookings = allBookingsSnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((b) => b.id !== excludeBookingId);

    for (const occurrence of occurrences) {
      const formattedDate = formatDateGMT7(occurrence);

      for (const existingBooking of allBookings) {
        let conflictDates = [];

        if (existingBooking.isRecurring) {
          try {
            const existingOccurrences = generateOccurrences(
              new Date(existingBooking.date),
              new Date(existingBooking.recurrenceEndDate),
              existingBooking.recurrenceType,
              existingBooking.recurrenceDays
            );
            conflictDates = existingOccurrences.map(formatDateGMT7);
          } catch {
            continue; // skip invalid recurrence
          }
        } else {
          conflictDates = [existingBooking.date];
        }

        if (!conflictDates.includes(formattedDate)) continue;

        const [existingStartHours, existingStartMinutes] =
          existingBooking.startTime.split(":").map(Number);
        const [existingEndHours, existingEndMinutes] = existingBooking.endTime
          .split(":")
          .map(Number);
        const existingStart = existingStartHours * 60 + existingStartMinutes;
        const existingEnd = existingEndHours * 60 + existingEndMinutes;

        const overlap =
          (newStart >= existingStart && newStart <= existingEnd) ||
          (newEnd >= existingStart && newEnd <= existingEnd) ||
          (newStart <= existingStart && newEnd >= existingEnd);

        if (overlap) {
          throw new Error(
            `Conflict on ${formattedDate} (${existingBooking.startTime} - ${existingBooking.endTime})`
          );
        }
      }
    }

    return true;
  } catch (err) {
    if (err.code === "permission-denied") {
      throw new Error("You don’t have permission to access this room");
    }
    if (err.code === "unavailable") {
      throw new Error("Network error, please try again");
    }
    throw new Error(err.message || "Error checking availability");
  }
};
